import type { Glyph, VariationCoords } from 'opentype.js';
import type { LoadedFont } from '../fonts/fontLoader';
import { DEG, matMul, matRotate, matScale, matScaleMagnitude, matSkew, matTranslate, type Mat2D, type Rect } from '../math';
import { flattenCommands, type Contour } from './flatten';
import type { BaselineSettings, TextSettings } from './settings';

/** One positioned glyph, in canvas pixel space. */
export interface GlyphInstance {
  /** Running index over the whole text, whitespace included. */
  index: number;
  char: string;
  line: number;
  /** Column within its line. */
  column: number;
  isWhitespace: boolean;
  /** Baseline origin in canvas space. */
  originX: number;
  originY: number;
  /** Rotation applied to this glyph in radians (baseline tangent + block rotation). */
  rotation: number;
  /** Advance width in canvas px, tracking and word spacing included. */
  advance: number;
  contours: Contour[];
  bounds: Rect;
}

export interface LineMetrics {
  index: number;
  text: string;
  /** Natural width in text space, before justification. */
  width: number;
  glyphStart: number;
  glyphEnd: number;
}

export interface TextLayout {
  glyphs: GlyphInstance[];
  lines: LineMetrics[];
  /** Tight bounding box of all glyph contours in canvas space. */
  bounds: Rect;
  /** The block transform that maps text space to canvas space. */
  transform: Mat2D;
  /** Effective font size after auto-fit, in px. */
  effectiveFontSize: number;
  /** True when the requested characters produced no drawable outline at all. */
  isEmpty: boolean;
}

// ── Baseline curves ──────────────────────────────────────────────────────────

interface Placed {
  x: number;
  y: number;
  angle: number;
}

/**
 * Maps a position along the baseline (`s`, in text space px, 0 = line start after
 * alignment) plus a vertical line offset to a point and tangent angle.
 */
type BaselineCurve = (s: number, dy: number) => Placed;

function makeBaselineCurve(b: BaselineSettings): BaselineCurve {
  if (b.mode === 'arc' && Math.abs(b.arcRadius) >= 1) {
    const r = b.arcRadius;
    const offset = b.arcOffset * DEG;
    // Circle centred at (0, r): at theta = 0 the baseline touches the origin and runs
    // horizontally, so an arc radius approaching infinity degrades to the straight case.
    return (s, dy) => {
      const theta = s / r + offset;
      const radius = r - dy;
      return {
        x: radius * Math.sin(theta),
        y: r - radius * Math.cos(theta),
        angle: theta,
      };
    };
  }
  if (b.mode === 'wave') {
    const k = (Math.PI * 2 * b.waveFrequency) / 1000;
    const phase = b.wavePhase * DEG;
    return (s, dy) => ({
      x: s,
      y: dy + b.waveAmplitude * Math.sin(k * s + phase),
      angle: Math.atan(b.waveAmplitude * k * Math.cos(k * s + phase)),
    });
  }
  return (s, dy) => ({ x: s, y: dy, angle: 0 });
}

// ── Character preparation ────────────────────────────────────────────────────

interface PreparedChar {
  /** The character actually rendered (may be case-folded). */
  render: string;
  /** The original character, kept for techniques that care (e.g. per-letter labels). */
  source: string;
  /** Size multiplier — < 1 for synthesised small caps. */
  scale: number;
}

function applyTextTransform(text: string, mode: TextSettings['transform'], smallCapsScale: number): PreparedChar[][] {
  return text.split('\n').map((line) =>
    [...line].map((ch): PreparedChar => {
      switch (mode) {
        case 'upper':
          return { render: ch.toUpperCase(), source: ch, scale: 1 };
        case 'lower':
          return { render: ch.toLowerCase(), source: ch, scale: 1 };
        case 'smallcaps': {
          // Synthetic small caps: real `smcp` needs GSUB feature application, which
          // opentype.js only supports for a subset of lookup types. Scaling the
          // capital is the standard fallback and behaves predictably across fonts.
          const upper = ch.toUpperCase();
          const isLower = upper !== ch && ch.toLowerCase() === ch;
          return { render: upper, source: ch, scale: isLower ? smallCapsScale : 1 };
        }
        default:
          return { render: ch, source: ch, scale: 1 };
      }
    }),
  );
}

// ── Layout ───────────────────────────────────────────────────────────────────

interface MeasuredGlyph {
  char: string;
  /**
   * The *base* glyph, never the variation-transformed one.
   *
   * `Glyph.getPath` derives its scale from `this.path.unitsPerEm`, and the object
   * returned by `variation.getTransform` does not carry that property — falling back
   * to 1000 and drawing a 2048-upm font 2.048× too large. Passing the base glyph with
   * `{ variation }` lets opentype.js apply the deltas internally while taking the
   * scale from a path that knows its units.
   */
  glyph: Glyph | null;
  scale: number;
  /** Advance in text-space px at fontSize 1 (i.e. em units), variation applied. */
  advanceEm: number;
  isWhitespace: boolean;
}

function measureLine(
  chars: PreparedChar[],
  font: LoadedFont,
  coords: VariationCoords,
  tracking: number,
  wordSpacing: number,
): { glyphs: MeasuredGlyph[]; widthEm: number } {
  const upm = font.unitsPerEm;
  const glyphs: MeasuredGlyph[] = [];
  let widthEm = 0;
  let previous: Glyph | null = null;

  for (const ch of chars) {
    const isWhitespace = /\s/.test(ch.render);
    let glyph: Glyph | null = null;
    let advanceEm = 0;
    try {
      const base = font.font.charToGlyph(ch.render);
      // getTransform resolves gvar/HVAR deltas, so the advance matches the varied
      // outline; the path itself is generated from `base` further down.
      const varied = font.axes.length ? font.font.variation.getTransform(base, coords) : base;
      glyph = base;
      advanceEm = (varied.advanceWidth / upm) * ch.scale;
      if (previous) {
        advanceEm += font.font.getKerningValue(previous, base) / upm;
      }
      previous = base;
    } catch {
      // Unmapped codepoint — reserve a blank of one quarter em so layout stays stable.
      advanceEm = 0.25;
      previous = null;
    }
    advanceEm += tracking;
    if (isWhitespace) advanceEm += wordSpacing;

    glyphs.push({ char: ch.render, glyph: isWhitespace ? null : glyph, scale: ch.scale, advanceEm, isWhitespace });
    widthEm += advanceEm;
  }

  // Trailing tracking should not count towards the visual line width.
  if (glyphs.length) widthEm -= tracking;
  return { glyphs, widthEm };
}

function anchorOffset(anchor: 'left' | 'center' | 'right' | 'top' | 'middle' | 'bottom', size: number): number {
  if (anchor === 'left' || anchor === 'top') return 0;
  if (anchor === 'right' || anchor === 'bottom') return size;
  return size / 2;
}

/**
 * Lays the text out and bakes the full transform chain into per-glyph contours,
 * so everything downstream works in plain canvas pixels.
 */
export function layoutText(
  settings: TextSettings,
  font: LoadedFont,
  canvasWidth: number,
  canvasHeight: number,
): TextLayout {
  const coords: VariationCoords = {};
  for (const axis of font.axes) {
    coords[axis.tag] =
      axis.tag === 'wght'
        ? Math.min(axis.max, Math.max(axis.min, settings.weight))
        : Math.min(axis.max, Math.max(axis.min, settings.variation[axis.tag] ?? axis.default));
  }

  const preparedLines = applyTextTransform(settings.text, settings.transform, settings.smallCapsScale);
  const measured = preparedLines.map((chars) =>
    measureLine(chars, font, coords, settings.tracking, settings.wordSpacing),
  );

  const maxWidthEm = measured.reduce((m, l) => Math.max(m, l.widthEm), 0);

  // Auto-fit: the layout is scale-free in em, so the fitted size is a direct division.
  // Width is the primary constraint, but the height is clamped too — otherwise three
  // lines of one character each would fit the width and run far off the canvas.
  let fontSize = settings.fontSize;
  if (settings.autoFit && maxWidthEm > 1e-6) {
    const byWidth = (canvasWidth * settings.autoFitPercent) / maxWidthEm;
    const blockHeightEm =
      settings.lineHeight * (measured.length - 1) + (font.ascender - font.descender) / font.unitsPerEm;
    const byHeight = (canvasHeight * settings.autoFitPercent) / Math.max(1e-6, blockHeightEm);
    fontSize = Math.min(byWidth, byHeight);
  }

  const lineStep = settings.lineHeight * fontSize;
  const curve = makeBaselineCurve(settings.baseline);

  // ── Block transform: anchor → offset → rotate → skew ─────────────────────
  const blockHeight = lineStep * (measured.length - 1) + fontSize;
  const anchorPx = {
    x: anchorOffset(settings.anchorX, canvasWidth),
    y: anchorOffset(settings.anchorY, canvasHeight),
  };
  let transform = matTranslate(
    anchorPx.x + settings.offsetX * canvasWidth,
    anchorPx.y + settings.offsetY * canvasHeight,
  );
  transform = matMul(transform, matRotate(settings.rotation * DEG));
  transform = matMul(transform, matSkew(settings.skewX * DEG, settings.skewY * DEG));
  // Centre the block on the anchor point vertically using cap-ish metrics, and
  // horizontally using the widest line, so anchoring feels optical rather than
  // baseline-relative.
  const ascent = (font.ascender / font.unitsPerEm) * fontSize;
  const descent = (font.descender / font.unitsPerEm) * fontSize;
  const blockTop = -ascent;
  const yAlign =
    settings.anchorY === 'top' ? -blockTop
    : settings.anchorY === 'bottom' ? -(blockTop + blockHeight - descent)
    : -(blockTop + (lineStep * (measured.length - 1) + ascent - descent) / 2);
  transform = matMul(transform, matTranslate(0, yAlign));

  const scaleMagnitude = matScaleMagnitude(transform) || 1;
  const flattenTolerance = settings.outlineTolerance / scaleMagnitude;

  const glyphs: GlyphInstance[] = [];
  const lines: LineMetrics[] = [];
  let runningIndex = 0;

  measured.forEach((line, lineIndex) => {
    const naturalWidth = line.widthEm * fontSize;
    const targetWidth = maxWidthEm * fontSize;

    let startX: number;
    let extraPerSpace = 0;
    let extraPerGlyph = 0;
    switch (settings.align) {
      case 'center':
        startX = -naturalWidth / 2;
        break;
      case 'right':
        startX = -naturalWidth;
        break;
      case 'justify': {
        startX = -targetWidth / 2;
        const slack = targetWidth - naturalWidth;
        const spaces = line.glyphs.filter((g) => g.isWhitespace).length;
        if (spaces > 0) extraPerSpace = slack / spaces;
        else if (line.glyphs.length > 1) extraPerGlyph = slack / (line.glyphs.length - 1);
        break;
      }
      default:
        startX = 0;
    }

    const glyphStart = glyphs.length;
    const lineY = lineStep * lineIndex;
    let pen = startX;

    line.glyphs.forEach((mg, column) => {
      const advance = mg.advanceEm * fontSize + (mg.isWhitespace ? extraPerSpace : 0) + extraPerGlyph;
      const placed = curve(pen, lineY);
      const glyphAngle = settings.baseline.followTangent ? placed.angle : 0;

      let contours: Contour[] = [];
      let bounds: Rect = { x: placed.x, y: placed.y, width: 0, height: 0 };

      if (mg.glyph && !mg.isWhitespace) {
        // Glyph-local transform: rotate onto the baseline tangent, then move into
        // text space; the block matrix is composed on top so we only flatten once.
        let local = matTranslate(placed.x, placed.y);
        if (glyphAngle !== 0) local = matMul(local, matRotate(glyphAngle));
        if (mg.scale !== 1) local = matMul(local, matScale(mg.scale, mg.scale));
        const full = matMul(transform, local);

        const path = mg.glyph.getPath(0, 0, fontSize, { variation: coords }, font.font);
        contours = flattenCommands(path.commands, flattenTolerance, full);
        if (contours.length) {
          let gx1 = Infinity, gy1 = Infinity, gx2 = -Infinity, gy2 = -Infinity;
          for (const c of contours) {
            if (c.bounds.x < gx1) gx1 = c.bounds.x;
            if (c.bounds.y < gy1) gy1 = c.bounds.y;
            if (c.bounds.x + c.bounds.width > gx2) gx2 = c.bounds.x + c.bounds.width;
            if (c.bounds.y + c.bounds.height > gy2) gy2 = c.bounds.y + c.bounds.height;
          }
          bounds = { x: gx1, y: gy1, width: gx2 - gx1, height: gy2 - gy1 };
        }
      }

      const origin = { x: placed.x, y: placed.y };
      const originCanvas = {
        x: transform.a * origin.x + transform.c * origin.y + transform.e,
        y: transform.b * origin.x + transform.d * origin.y + transform.f,
      };

      glyphs.push({
        index: runningIndex++,
        char: mg.char,
        line: lineIndex,
        column,
        isWhitespace: mg.isWhitespace,
        originX: originCanvas.x,
        originY: originCanvas.y,
        rotation: glyphAngle + settings.rotation * DEG,
        advance: advance * scaleMagnitude,
        contours,
        bounds,
      });

      pen += advance;
    });

    lines.push({
      index: lineIndex,
      text: preparedLines[lineIndex].map((c) => c.source).join(''),
      width: naturalWidth,
      glyphStart,
      glyphEnd: glyphs.length,
    });
    runningIndex++; // reserve an index for the newline
  });

  // Tight bounds over every contour vertex.
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let vertexCount = 0;
  for (const g of glyphs) {
    for (const c of g.contours) {
      const p = c.points;
      vertexCount += p.length / 2;
      for (let i = 0; i < p.length; i += 2) {
        if (p[i] < minX) minX = p[i];
        if (p[i] > maxX) maxX = p[i];
        if (p[i + 1] < minY) minY = p[i + 1];
        if (p[i + 1] > maxY) maxY = p[i + 1];
      }
    }
  }
  const isEmpty = vertexCount === 0;
  const bounds: Rect = isEmpty
    ? { x: 0, y: 0, width: 0, height: 0 }
    : { x: minX, y: minY, width: maxX - minX, height: maxY - minY };

  return { glyphs, lines, bounds, transform, effectiveFontSize: fontSize, isEmpty };
}

export function allContours(layout: TextLayout): Contour[] {
  const out: Contour[] = [];
  for (const g of layout.glyphs) out.push(...g.contours);
  return out;
}
